import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  setCanvasDisplayMode,
  setCanvasImageScoreView,
  setCanvasTabDisplayMode,
  setCanvasWidthPercentage,
  setGlobalData,
  setIsFetchingGlobalData,
  setShowLicenseModal,
  setTooltip,
  updateTooltipPosition,
} from 'redux/reducers/commonReducer';
import { CanvasTooltipProps } from 'components/canvas/tooltip';
import { DisplayMode } from 'components/Experiment/constants';
import { CaptureProfileDTO, ExpGlobalDataDTO } from 'business/models/Models';
import {
  allImagingSettingsThunk,
  canvasPreferenceThunk,
  scoreGroupDetailsThunk,
  userPreferencesThunk,
} from 'redux/thunks/common';
import { useMemo } from 'react';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import { MetaDetaExplorerTree } from 'constants/propertyMetaDeta';
import { getAllScoreGroups } from 'utils/Global';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';

export const useCanvasDisplayMode = (): keyof typeof DisplayMode => {
  return useRMSelector((state) => state?.common.canvas.displayMode);
};
export const useCanvasNavigationTabDisplayMode = (): keyof typeof DisplayMode => {
  return useRMSelector((state) => state?.common.canvas.NavigationTabDisplayMode);
};

export const useSetCanvasDisplayMode = (): ((
  _displayMode: keyof typeof DisplayMode,
) => void) => {
  const dispatch = useRMDispatch();
  return (_displayMode: keyof typeof DisplayMode) => {
    dispatch(setCanvasDisplayMode(_displayMode));
  };
};

export const useSetCanvasNavigationTapDisplayMode = (): ((
  _displayMode: keyof typeof DisplayMode,
) => void) => {
  const dispatch = useRMDispatch();

  return (_displayMode: keyof typeof DisplayMode) => {
    dispatch(setCanvasTabDisplayMode(_displayMode));
  };
};

export const useSetCanvasWidthPercentage = (): ((percentage: number) => void) => {
  const dispatch = useRMDispatch();

  return (percentage: number) => {
    dispatch(setCanvasWidthPercentage(percentage));
  };
};

export const useCanvasImageScoreView = (): keyof typeof DisplayMode => {
  return useRMSelector((state) => state?.common.canvas.imageScoreView);
};

export const useSetCanvasImageScoreView = (): ((
  view: keyof typeof DisplayMode,
) => void) => {
  const dispatch = useRMDispatch();
  return (view: keyof typeof DisplayMode) => {
    dispatch(setCanvasImageScoreView(view));
  };
};

export const useSetTooltip = (): ((tooltip: CanvasTooltipProps) => void) => {
  const dispatch = useRMDispatch();
  return (tooltip: CanvasTooltipProps) => {
    dispatch(setTooltip(tooltip));
  };
};

export const useUpdateTooltipPointerPosition = (): ((
  pointerX: number,
  pointerY: number,
) => void) => {
  const dispatch = useRMDispatch();
  return (pointerX: number, pointerY) => {
    dispatch(updateTooltipPosition({ pointerX, pointerY }));
  };
};

export const useTooltipData = () => {
  return useRMSelector((state) => state?.common.tooltip);
};

export const useShowLicenseModal = () => {
  return useRMSelector((state) => state?.common.showLicensModal);
};

export const useSetShowLicenseModal = () => {
  const dispatch = useRMDispatch();
  return (show: boolean) => {
    dispatch(setShowLicenseModal(show));
  };
};

export const useCanvasWidthPercentage = (): number => {
  return useRMSelector((state) => state?.common.canvas.canvasWidthPercentage);
};

export const useSetIsFetchingGlobalData = () => {
  const dispatch = useRMDispatch();
  return (isFetching: boolean) => {
    dispatch(setIsFetchingGlobalData(isFetching));
  };
};

export const useSetGlobalData = () => {
  const dispatch = useRMDispatch();
  return (data: ExpGlobalDataDTO) => {
    dispatch(setGlobalData(data));
  };
};

export const useIsFetchingGlobalData = (): boolean => {
  return useRMSelector((state) => state?.common.globalData.isFetching);
};

export const useGlobalData = () => {
  return useRMSelector((state) => state?.common.globalData);
};

export const useDefaultSelectedSearchFields = () => {
  return useRMSelector((state) => state.common.defaultSelectedSearchFieldsMap);
};

export const useFetchsearchGroupDetails = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(scoreGroupDetailsThunk());
  };
};

export const useFetchAllImagingSettings = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(allImagingSettingsThunk());
  };
};

export const useFetchUserPreferences = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(userPreferencesThunk());
  };
};

const defaultAutoScorerName = 'MARCO';
const defaultCaptureProfileName = 'Visible (Default)';
const defaultAutoScorerRelatedKeyedValues = {
  [MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically]: false,
  [MetaDetaExplorerTree.TopAutoScoreImageGroup]: 1,
  [MetaDetaExplorerTree.TopAutoScoreImageSetting]: 1,
  [MetaDetaExplorerTree.TopAutoScoreImageListValue]: 100,
};

export const useUserTopAutoScoreSortSettings = () => {
  const imageSettings = useAllImagingSettings();
  const scoreGroups = getAllScoreGroups()
    ?.filter((x) => x.isAutoScore)
    ?.map((x) => ({ title: x.name, value: x.scoreGroupsId }));
  const topAutoScoreImageSearchAutomatically = useSystemProperty(
    MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically,
    false,
  );
  const topAutoScoreImageGroup = useSystemProperty(
    MetaDetaExplorerTree.TopAutoScoreImageGroup,
    1,
  );
  const topAutoScoreImageSetting = useSystemProperty(
    MetaDetaExplorerTree.TopAutoScoreImageSetting,
    1,
  );
  const topAutoScoreImageListValue = useSystemProperty(
    MetaDetaExplorerTree.TopAutoScoreImageListValue,
    100,
  );

  const getDefaultCpId = () => {
    const defaultProfile = imageSettings.captureProfileRows.find(
      (x) => x.name === defaultCaptureProfileName,
    );
    if (defaultProfile) {
      return defaultProfile.id;
    }

    // if default Capture Profile (Visible (Default)) is not found then find Capture Profile by lowest Id (oldest)
    return imageSettings.captureProfileRows.reduce((a, b) => (a.id < b.id ? a : b)).id;
  };

  return useMemo(() => {
    if (!scoreGroups || !imageSettings) {
      return {};
    }
    // set default values if value is '-1'// which means default value was not set incase of a Dropdown
    defaultAutoScorerRelatedKeyedValues[MetaDetaExplorerTree.TopAutoScoreImageGroup] =
      scoreGroups.find((x) => x.title === defaultAutoScorerName)?.value;

    defaultAutoScorerRelatedKeyedValues[MetaDetaExplorerTree.TopAutoScoreImageSetting] =
      getDefaultCpId();

    return [
      {
        key: MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically,
        value: topAutoScoreImageSearchAutomatically,
      },
      {
        key: MetaDetaExplorerTree.TopAutoScoreImageGroup,
        value:
          topAutoScoreImageGroup === '-1'
            ? defaultAutoScorerRelatedKeyedValues[
                MetaDetaExplorerTree.TopAutoScoreImageGroup
              ]
            : topAutoScoreImageGroup,
      },
      {
        key: MetaDetaExplorerTree.TopAutoScoreImageSetting,
        value:
          topAutoScoreImageSetting === '-1'
            ? defaultAutoScorerRelatedKeyedValues[
                MetaDetaExplorerTree.TopAutoScoreImageSetting
              ]
            : topAutoScoreImageSetting,
      },
      {
        key: MetaDetaExplorerTree.TopAutoScoreImageListValue,
        value:
          topAutoScoreImageListValue > 100 || topAutoScoreImageListValue < 1
            ? defaultAutoScorerRelatedKeyedValues[
                MetaDetaExplorerTree.TopAutoScoreImageListValue
              ]
            : topAutoScoreImageListValue,
      },
    ].reduce((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {});
  }, [
    topAutoScoreImageSearchAutomatically,
    topAutoScoreImageGroup,
    topAutoScoreImageSetting,
    topAutoScoreImageListValue,
    imageSettings,
  ]);
};

export const useScoreDefinitionDTO = () => {
  return useRMSelector((state) => state?.common?.scoreDefinition);
};

export const useScoreGroupDetailsScore = () => {
  return useRMSelector((state) => state?.common?.searchGroupDetails?.score);
};

export const useScoreGroupDetailsGroup = () => {
  return useRMSelector((state) => state?.common?.searchGroupDetails?.group);
};

export const useScoreGroups = () => {
  return useRMSelector((state) => state?.common?.scoreGroups);
};

export const useIsZoomLocked = (): boolean => {
  return useRMSelector((state) => state?.common.canvas.isZoomLocked);
};

export const useAllImagingSettings = (): CaptureProfileDTO => {
  return useRMSelector((state) => state?.common.allImagingSettings);
};

export const useIsScreenInUse = (): boolean => {
  const screenRepairInfo = useRMSelector(
    (state) => state?.experiment?.expData?.screenRepairInfo,
  );
  return screenRepairInfo?.totalLayersUsingScreen > 0 ? true : false;
};

export const useIsScreenIngredientEditable = (): boolean => {
  const screenRepairInfo = useRMSelector(
    (state) => state?.experiment?.expData?.screenRepairInfo,
  );

  if (!screenRepairInfo || screenRepairInfo?.totalLayersUsingScreen == 0) {
    return true;
  }

  return Object.keys(screenRepairInfo?.experimentInfo || {}).length > 0
    ? screenRepairInfo.isScreenRepairEnabled
    : true;
};

export const useManualScoreGroups = () => {
  return useRMSelector((state) => state.common.globalData?.data?.manualScoreGroups);
};

export const useAllScoresGroups = () => {
  return useRMSelector((state) => state.common.globalData?.data?.allScoresGroups);
};

export const useCanvasPreference = (preferenceKey: ECanvasPreferencesKeys) => {
  return useRMSelector((state) => state.common.canvas?.canvasPreferences[preferenceKey]);
};

export const useFetchExperimentCanvasPreferences = () => {
  const dispatch = useRMDispatch();
  return (propertyKeys: string[]) => {
    dispatch(canvasPreferenceThunk(propertyKeys));
  };
};

export const useCookieCounter = () => {
  return useRMSelector((state) => state?.common?.cookieCounter);
};

export const useSelectedGroup = () => {
  return useRMSelector((state) => state?.common?.user.selectedGroup);
};

export const useSystemState = () => {
  return useRMSelector((state) => state?.common?.systemState);
};

export const useLicenseValidatorPublicKey = () => {
  return useRMSelector((state) => state?.common?.licenseValidatorPublicKey);
};

export const useScoreGroupsForScoringTool = () => {
  const allScoresGroups = useRMSelector(
    (state) => state?.common?.globalData?.data?.allScoresGroups,
  );
  return useMemo(
    () =>
      allScoresGroups?.filter(
        (sg) =>
          sg.isAutoScore === false ||
          (sg.isAutoScore === true && sg.unit == null && sg.scoreGroupType === 0),
      ) ?? [],
    [allScoresGroups],
  );
};
